import {
  AddIcon,
  CheckCircleIcon,
  CloseIcon,
  ErrorIcon,
  ExpandMoreIcon,
  LightbulbIcon,
  MonitorHeartIcon,
  PlayArrowIcon,
  PsychologyIcon,
  RefreshIcon,
  ShieldIcon,
  TimelineIcon,
  WarningAmberIcon,
} from '../theme/icons';
import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  SreInvestigationSummary,
  SreInvestigationDetail,
  SreSessionInsight,
  SreRunbook,
  SreGuardrailPolicy,
} from '../types';
import {
  approveSreAction,
  deleteSreRunbook,
  getSreGuardrails,
  getSreInsights,
  getSreInvestigation,
  getSreInvestigations,
  getSreRunbooks,
  rejectSreAction,
  saveSreRunbook,
  startSreInvestigation,
} from '../services/api';

/**
 * SRE Agent dashboard.
 *
 * The layout follows the investigation itself: incident list → the reasoning
 * chain (hypotheses + evidence) → proposed mitigations awaiting approval.
 *
 * The evidence chain is the point of the whole feature, so it is shown by
 * default rather than hidden behind a "details" link — a root cause you can't
 * audit is worth very little during an incident.
 */

const severityColor = (s: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (s) {
    case 'Sev1': return 'error';
    case 'Sev2': return 'warning';
    case 'Sev3': return 'info';
    default: return 'default';
  }
};

const statusColor = (s: string): 'success' | 'warning' | 'error' | 'info' | 'default' => {
  switch (s) {
    case 'Resolved': return 'success';
    case 'AwaitingApproval': return 'warning';
    case 'Failed': return 'error';
    case 'Abandoned': return 'default';
    default: return 'info';
  }
};

const verdictColor = (v: string): 'success' | 'error' | 'warning' | 'default' => {
  switch (v) {
    case 'Supported': return 'success';
    case 'Refuted': return 'error';
    case 'Inconclusive': return 'warning';
    default: return 'default';
  }
};

const SreAgentDashboard: React.FC = () => {
  const [tab, setTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [investigations, setInvestigations] = useState<SreInvestigationSummary[]>([]);
  const [selected, setSelected] = useState<SreInvestigationDetail | null>(null);
  const [insights, setInsights] = useState<SreSessionInsight[]>([]);
  const [runbooks, setRunbooks] = useState<SreRunbook[]>([]);
  const [guardrails, setGuardrails] = useState<SreGuardrailPolicy | null>(null);

  const [newOpen, setNewOpen] = useState(false);
  const [newTitle, setNewTitle] = useState('');
  const [newAlert, setNewAlert] = useState('');
  const [newSeverity, setNewSeverity] = useState('Sev3');
  const [newResourceId, setNewResourceId] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [runbookOpen, setRunbookOpen] = useState(false);
  const [editingRunbook, setEditingRunbook] = useState<SreRunbook | null>(null);

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [inv, ins, rb, gr] = await Promise.all([
        getSreInvestigations(),
        getSreInsights(),
        getSreRunbooks(),
        getSreGuardrails(),
      ]);
      setInvestigations(inv);
      setInsights(ins);
      setRunbooks(rb);
      setGuardrails(gr);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? e?.message ?? 'Failed to load SRE data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const openInvestigation = async (investigationId: string) => {
    setLoading(true);
    try {
      setSelected(await getSreInvestigation(investigationId));
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not load the investigation.');
    } finally {
      setLoading(false);
    }
  };

  const handleStart = async () => {
    if (!newTitle.trim()) return;
    setSubmitting(true);
    setError(null);
    try {
      const inv = await startSreInvestigation({
        title: newTitle,
        alertSummary: newAlert,
        severity: newSeverity,
        resourceId: newResourceId || undefined,
      });
      setNewOpen(false);
      setNewTitle(''); setNewAlert(''); setNewResourceId('');
      await loadAll();
      await openInvestigation(inv.investigationId);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Investigation failed to start.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleApprove = async (actionId: number) => {
    setError(null);
    try {
      await approveSreAction(actionId);
      if (selected) await openInvestigation(selected.summary.investigationId);
      await loadAll();
    } catch (e: any) {
      // Guardrail refusals arrive here — show the reason verbatim.
      setError(e?.response?.data?.error ?? 'Approval failed.');
    }
  };

  const handleReject = async (actionId: number) => {
    try {
      await rejectSreAction(actionId, 'Rejected from dashboard');
      if (selected) await openInvestigation(selected.summary.investigationId);
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Rejection failed.');
    }
  };

  const handleSaveRunbook = async () => {
    if (!editingRunbook?.name?.trim()) return;
    try {
      await saveSreRunbook(editingRunbook);
      setRunbookOpen(false);
      setEditingRunbook(null);
      setRunbooks(await getSreRunbooks());
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not save the runbook.');
    }
  };

  const handleDeleteRunbook = async (id: number) => {
    try {
      await deleteSreRunbook(id);
      setRunbooks(await getSreRunbooks());
    } catch (e: any) {
      setError(e?.response?.data?.error ?? 'Could not delete the runbook.');
    }
  };

  const openCount = investigations.filter(i =>
    i.status !== 'Resolved' && i.status !== 'Abandoned' && i.status !== 'Failed').length;
  const awaitingCount = investigations.filter(i => i.status === 'AwaitingApproval').length;
  const resolved = investigations.filter(i => i.timeToResolveMinutes != null);
  const mttr = resolved.length
    ? Math.round(resolved.reduce((a, b) => a + (b.timeToResolveMinutes ?? 0), 0) / resolved.length)
    : null;

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight={700} display="flex" alignItems="center" gap={1}>
            <PsychologyIcon /> SRE Agent
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Hypothesis-driven incident investigation with an auditable evidence chain.
          </Typography>
        </Box>
        <Stack direction="row" spacing={1}>
          <Button startIcon={<RefreshIcon />} onClick={loadAll} disabled={loading}>
            Refresh
          </Button>
          <Button variant="contained" startIcon={<PlayArrowIcon />} onClick={() => setNewOpen(true)}>
            Investigate
          </Button>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}
      {loading && <LinearProgress sx={{ mb: 2 }} />}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {[
          { label: 'Open investigations', value: openCount, icon: <MonitorHeartIcon /> },
          { label: 'Awaiting approval', value: awaitingCount, icon: <WarningAmberIcon /> },
          { label: 'Mean time to resolve', value: mttr != null ? `${mttr} min` : '—', icon: <TimelineIcon /> },
          { label: 'Lessons captured', value: insights.length, icon: <LightbulbIcon /> },
        ].map(c => (
          <Grid item xs={12} sm={6} md={3} key={c.label}>
            <Card>
              <CardContent>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                  {c.icon}
                  <Typography variant="body2" color="text.secondary">{c.label}</Typography>
                </Stack>
                <Typography variant="h4" fontWeight={700}>{c.value}</Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      <Tabs value={tab} onChange={(_, v) => { setTab(v); setSelected(null); }} sx={{ mb: 2 }}>
        <Tab label="Investigations" />
        <Tab label="Session insights" />
        <Tab label="Runbooks" />
        <Tab label="Guardrails" />
      </Tabs>

      {/* ── Investigations ─────────────────────────────────────────────── */}
      {tab === 0 && !selected && (
        <TableContainer component={Paper}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Incident</TableCell>
                <TableCell>Severity</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Root cause</TableCell>
                <TableCell align="right">Confidence</TableCell>
                <TableCell>Detected</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {investigations.length === 0 && (
                <TableRow>
                  <TableCell colSpan={6}>
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3, textAlign: 'center' }}>
                      No investigations yet. Start one manually, or point an Azure Monitor
                      action group at <code>/api/sre/intake/azure-monitor</code>.
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
              {investigations.map(i => (
                <TableRow
                  key={i.investigationId}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => openInvestigation(i.investigationId)}
                >
                  <TableCell>
                    <Typography variant="body2" fontWeight={600}>{i.title}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {i.source} · {i.investigationId}
                    </Typography>
                  </TableCell>
                  <TableCell><Chip size="small" label={i.severity} color={severityColor(i.severity)} /></TableCell>
                  <TableCell><Chip size="small" label={i.status} color={statusColor(i.status)} /></TableCell>
                  <TableCell sx={{ maxWidth: 320 }}>
                    <Typography variant="body2" noWrap>{i.rootCause ?? '—'}</Typography>
                  </TableCell>
                  <TableCell align="right">
                    {i.confidencePercent != null ? (
                      <Chip
                        size="small"
                        label={`${i.confidencePercent}%`}
                        color={i.confidencePercent >= 70 ? 'success' : i.confidencePercent >= 40 ? 'warning' : 'default'}
                      />
                    ) : '—'}
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{new Date(i.detectedAt).toLocaleString()}</Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* ── Investigation detail: the evidence chain ────────────────────── */}
      {tab === 0 && selected && (
        <Box>
          <Button startIcon={<CloseIcon />} onClick={() => setSelected(null)} sx={{ mb: 2 }}>
            Back to all investigations
          </Button>

          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <Chip size="small" label={selected.summary.severity} color={severityColor(selected.summary.severity)} />
                <Chip size="small" label={selected.summary.status} color={statusColor(selected.summary.status)} />
                <Chip size="small" variant="outlined" label={selected.summary.source} />
              </Stack>
              <Typography variant="h6" fontWeight={700}>{selected.summary.title}</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {selected.summary.alertSummary}
              </Typography>

              {selected.summary.rootCause && (
                <Alert
                  severity={(selected.summary.confidencePercent ?? 0) >= 70 ? 'success' : 'warning'}
                  sx={{ mb: 1 }}
                >
                  <Typography variant="subtitle2" fontWeight={700}>
                    Root cause ({selected.summary.confidencePercent ?? 0}% confidence)
                  </Typography>
                  <Typography variant="body2">{selected.summary.rootCause}</Typography>
                  {selected.summary.narrative && (
                    <Typography variant="body2" sx={{ mt: 1 }}>{selected.summary.narrative}</Typography>
                  )}
                </Alert>
              )}

              {selected.summary.errorMessage && (
                <Alert severity="error">{selected.summary.errorMessage}</Alert>
              )}
            </CardContent>
          </Card>

          {/* Proposed mitigations — the approval gate */}
          {selected.proposedActions.length > 0 && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700} gutterBottom
                  display="flex" alignItems="center" gap={1}>
                  <ShieldIcon fontSize="small" /> Proposed mitigations
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Nothing runs until you approve it. Guardrails are re-checked at execution time.
                </Typography>
                <Divider sx={{ my: 1.5 }} />
                <Stack spacing={1.5}>
                  {selected.proposedActions.map(a => (
                    <Paper key={a.id} variant="outlined" sx={{ p: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
                        <Box sx={{ flex: 1 }}>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Chip size="small" label={a.actionKind} />
                            <Chip
                              size="small"
                              label={a.state}
                              color={
                                a.state === 'Executed' ? 'success'
                                  : a.state === 'Rejected' || a.state === 'ExecutionFailed' ? 'error'
                                  : 'warning'
                              }
                            />
                          </Stack>
                          <Typography variant="body2" sx={{ mt: 0.5 }}>{a.description}</Typography>
                          {a.rationale && (
                            <Typography variant="caption" color="text.secondary" display="block">
                              Why: {a.rationale}
                            </Typography>
                          )}
                          {a.guardrailVerdict && (
                            <Tooltip title="Guardrail evaluation recorded at proposal time">
                              <Typography variant="caption" color="text.secondary" display="block">
                                🛡 {a.guardrailVerdict}
                              </Typography>
                            </Tooltip>
                          )}
                          {a.executionResult && (
                            <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                              Result: {a.executionResult}
                            </Typography>
                          )}
                        </Box>
                        {a.state === 'Pending' && (
                          <Stack direction="row" spacing={1}>
                            <Button size="small" variant="contained" color="primary"
                              onClick={() => handleApprove(a.id)}>
                              Approve &amp; run
                            </Button>
                            <Button size="small" color="inherit" onClick={() => handleReject(a.id)}>
                              Reject
                            </Button>
                          </Stack>
                        )}
                      </Stack>
                    </Paper>
                  ))}
                </Stack>
              </CardContent>
            </Card>
          )}

          {/* Hypotheses + their evidence */}
          <Card>
            <CardContent>
              <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                Reasoning chain
              </Typography>
              {selected.hypotheses.length === 0 && (
                <Typography variant="body2" color="text.secondary">
                  No hypotheses were formed.
                </Typography>
              )}
              {selected.hypotheses.map(h => {
                const linked = selected.evidence.filter(e => e.hypothesisId === h.id);
                return (
                  <Accordion key={h.id} disableGutters>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                        <Chip size="small" label={`#${h.rank}`} />
                        <Chip size="small" label={h.verdict} color={verdictColor(h.verdict)} />
                        <Typography variant="body2" sx={{ flex: 1 }}>{h.statement}</Typography>
                      </Stack>
                    </AccordionSummary>
                    <AccordionDetails>
                      {h.validationNotes && (
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          {h.validationNotes}
                        </Typography>
                      )}
                      {linked.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          No evidence was linked to this hypothesis.
                        </Typography>
                      ) : (
                        <Stack spacing={1}>
                          {linked.map(e => (
                            <Stack key={e.id} direction="row" spacing={1} alignItems="flex-start">
                              {e.supports === true
                                ? <CheckCircleIcon fontSize="small" color="success" />
                                : e.supports === false
                                  ? <ErrorIcon fontSize="small" color="error" />
                                  : <LightbulbIcon fontSize="small" />}
                              <Box>
                                <Typography variant="body2">{e.summary}</Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {e.kind} · {e.source}
                                </Typography>
                              </Box>
                            </Stack>
                          ))}
                        </Stack>
                      )}
                    </AccordionDetails>
                  </Accordion>
                );
              })}

              <Divider sx={{ my: 2 }} />
              <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                All signals gathered ({selected.evidence.length})
              </Typography>
              <Stack spacing={0.5}>
                {selected.evidence.map(e => (
                  <Typography key={e.id} variant="caption" color="text.secondary">
                    [{e.kind}] {e.summary}
                  </Typography>
                ))}
              </Stack>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* ── Session insights ───────────────────────────────────────────── */}
      {tab === 1 && (
        <Stack spacing={2}>
          {insights.length === 0 && (
            <Typography variant="body2" color="text.secondary">
              No post-incident insights yet. They are written automatically when an
              investigation concludes, and recalled during future incidents.
            </Typography>
          )}
          {insights.map(s => (
            <Card key={s.id}>
              <CardContent>
                <Typography variant="subtitle1" fontWeight={700}>{s.title}</Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(s.createdAt).toLocaleString()}
                  {s.resourceType ? ` · ${s.resourceType}` : ''}
                </Typography>

                {s.whatHappened && (
                  <Typography variant="body2" sx={{ mt: 1.5 }}>{s.whatHappened}</Typography>
                )}

                <Grid container spacing={2} sx={{ mt: 0.5 }}>
                  {s.whatWentWell && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" fontWeight={700}>What went well</Typography>
                      <Typography variant="body2">{s.whatWentWell}</Typography>
                    </Grid>
                  )}
                  {s.whatToImprove && (
                    <Grid item xs={12} md={6}>
                      <Typography variant="caption" fontWeight={700}>What to improve</Typography>
                      <Typography variant="body2">{s.whatToImprove}</Typography>
                    </Grid>
                  )}
                </Grid>

                {s.timeline.length > 0 && (
                  <Accordion disableGutters sx={{ mt: 1.5 }}>
                    <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                      <Typography variant="caption" fontWeight={700}>
                        Timeline ({s.timeline.length} events)
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack spacing={0.5}>
                        {s.timeline.map((t, idx) => (
                          <Typography key={idx} variant="caption">
                            <strong>{new Date(t.at).toLocaleTimeString()}</strong> — {t.event}
                            {t.detail ? `: ${t.detail}` : ''}
                          </Typography>
                        ))}
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                )}
              </CardContent>
            </Card>
          ))}
        </Stack>
      )}

      {/* ── Runbooks ───────────────────────────────────────────────────── */}
      {tab === 2 && (
        <Box>
          <Button
            startIcon={<AddIcon />}
            variant="outlined"
            sx={{ mb: 2 }}
            onClick={() => {
              setEditingRunbook({
                id: 0, name: '', content: '', isEnabled: true,
              } as SreRunbook);
              setRunbookOpen(true);
            }}
          >
            New runbook
          </Button>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Runbooks are injected into the agent's reasoning, so it follows your
            procedures instead of generic troubleshooting.
          </Typography>

          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Name</TableCell>
                  <TableCell>Applies to</TableCell>
                  <TableCell>Enabled</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {runbooks.map(r => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="body2" fontWeight={600}>{r.name}</Typography>
                      <Typography variant="caption" color="text.secondary">{r.description}</Typography>
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption">
                        {r.appliesToResourceType || r.appliesToAlertPattern || 'Any incident'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip size="small" label={r.isEnabled ? 'Yes' : 'No'}
                        color={r.isEnabled ? 'success' : 'default'} />
                    </TableCell>
                    <TableCell align="right">
                      <Button size="small" onClick={() => { setEditingRunbook(r); setRunbookOpen(true); }}>
                        Edit
                      </Button>
                      <Button size="small" color="error" onClick={() => handleDeleteRunbook(r.id)}>
                        Delete
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
                {runbooks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={4}>
                      <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                        No runbooks yet.
                      </Typography>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {/* ── Guardrails ─────────────────────────────────────────────────── */}
      {tab === 3 && guardrails && (
        <Card>
          <CardContent>
            <Typography variant="subtitle1" fontWeight={700} gutterBottom
              display="flex" alignItems="center" gap={1}>
              <ShieldIcon fontSize="small" /> Enforced safety policy
            </Typography>

            <Stack spacing={1} sx={{ mb: 2 }}>
              {guardrails.notes.map((n, i) => (
                <Alert key={i} severity="info" variant="outlined">{n}</Alert>
              ))}
            </Stack>

            <Typography variant="subtitle2" fontWeight={700} gutterBottom>
              Actions the agent may propose
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
              {guardrails.allowedActions.map(a => (
                <Chip key={a} size="small" label={a}
                  color={a.startsWith('Read') ? 'default' : 'primary'} />
              ))}
            </Box>

            <Typography variant="caption" color="text.secondary">
              Maximum scale-out target: {guardrails.maxScaleOutInstances} instances.
              Delete, purge, deallocate, terminate and credential-rotation operations
              are refused unconditionally and cannot be enabled.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* ── New investigation dialog ───────────────────────────────────── */}
      <Dialog open={newOpen} onClose={() => setNewOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Start an investigation</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField label="Title" fullWidth value={newTitle}
              onChange={e => setNewTitle(e.target.value)} autoFocus />
            <TextField label="What is happening?" fullWidth multiline rows={3}
              value={newAlert} onChange={e => setNewAlert(e.target.value)}
              placeholder="e.g. Container App returning 500s since 14:20 UTC" />
            <TextField select label="Severity" value={newSeverity}
              onChange={e => setNewSeverity(e.target.value)}>
              {['Sev1', 'Sev2', 'Sev3', 'Sev4'].map(s => (
                <MenuItem key={s} value={s}>{s}</MenuItem>
              ))}
            </TextField>
            <TextField label="Resource ID (optional)" fullWidth value={newResourceId}
              onChange={e => setNewResourceId(e.target.value)}
              placeholder="/subscriptions/.../resourceGroups/.../providers/..." />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNewOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleStart}
            disabled={submitting || !newTitle.trim()}>
            {submitting ? <CircularProgress size={20} /> : 'Investigate'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Runbook editor ─────────────────────────────────────────────── */}
      <Dialog open={runbookOpen} onClose={() => setRunbookOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>{editingRunbook?.id ? 'Edit runbook' : 'New runbook'}</DialogTitle>
        <DialogContent>
          {editingRunbook && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <TextField label="Name" fullWidth value={editingRunbook.name}
                onChange={e => setEditingRunbook({ ...editingRunbook, name: e.target.value })} />
              <TextField label="Description" fullWidth value={editingRunbook.description ?? ''}
                onChange={e => setEditingRunbook({ ...editingRunbook, description: e.target.value })} />
              <TextField label="Applies to resource type (optional)" fullWidth
                value={editingRunbook.appliesToResourceType ?? ''}
                placeholder="Microsoft.App/containerApps"
                onChange={e => setEditingRunbook({ ...editingRunbook, appliesToResourceType: e.target.value })} />
              <TextField label="Applies to alert containing (optional)" fullWidth
                value={editingRunbook.appliesToAlertPattern ?? ''}
                placeholder="500"
                onChange={e => setEditingRunbook({ ...editingRunbook, appliesToAlertPattern: e.target.value })} />
              <TextField label="Procedure (markdown)" fullWidth multiline rows={10}
                value={editingRunbook.content}
                onChange={e => setEditingRunbook({ ...editingRunbook, content: e.target.value })} />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRunbookOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveRunbook}>Save</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SreAgentDashboard;
